import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "signatureInput",
    "dependentsSection",
    "questionInput", "doesNotApplyCheckbox",
    "questionnaireTabButton",
    "bottomSubmitButton", "bottomSubmitStatus"
  ]

  static values = {
    signUrl: String,
    submitUrl: String,
    canSubmit: Boolean,
    minimumProgress: Number
  }

  connect() {
    // Set up collapse event listeners for chevron icon updates
    this.setupCollapseEventListeners()
  }

  disconnect() {
    // Nothing to clean up
  }

  // Handle question focus - prevent event from bubbling to section headers
  handleQuestionFocus(event) {
    // Prevent event from bubbling up to section headers
    event.stopPropagation()
  }

  // Handle section toggle - update chevron icon when section is expanded/collapsed
  handleSectionToggle(event) {
    const clickedHeader = event.currentTarget
    const targetId = clickedHeader.getAttribute('data-bs-target')
    const collapseElement = document.querySelector(targetId)

    if (collapseElement) {
      const isExpanded = clickedHeader.getAttribute('aria-expanded') === 'true'

      if (isExpanded) {
        // Collapse the section
        const bsCollapse = new bootstrap.Collapse(collapseElement, { toggle: false })
        bsCollapse.hide()
        clickedHeader.setAttribute('aria-expanded', 'false')
        this.updateSectionIcons(clickedHeader, false)
      } else {
        // Expand the section
        const bsCollapse = new bootstrap.Collapse(collapseElement, { toggle: false })
        bsCollapse.show()
        clickedHeader.setAttribute('aria-expanded', 'true')
        this.updateSectionIcons(clickedHeader, true)
      }
    }
  }

  // Update section icons (chevron and folder) when toggling
  updateSectionIcons(header, isExpanded) {
    // Update chevron - Font Awesome replaces <i> with <svg>, so target the SVG
    const chevronSvg = header.querySelector('svg.fa-chevron-up, svg.fa-chevron-down')
    if (chevronSvg) {
      // Rotate the existing SVG
      chevronSvg.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'
      chevronSvg.style.transition = 'transform 0.2s ease'
    }

    // Update folder icon
    const folderSvg = header.querySelector('svg.fa-folder, svg.fa-folder-open')
    if (folderSvg) {
      // Toggle folder icon class
      if (isExpanded) {
        folderSvg.classList.remove('fa-folder')
        folderSvg.classList.add('fa-folder-open')
      } else {
        folderSvg.classList.remove('fa-folder-open')
        folderSvg.classList.add('fa-folder')
      }
    }
  }

  // Toggle "Does Not Apply" checkbox and manage related input
  toggleDoesNotApply(event) {
    const checkbox = event.target
    const questionId = checkbox.dataset.questionId
    const isDependentsQuestion = checkbox.dataset.dependentsQuestion === 'true'

    // Dependents DNA is now handled by tax-organizer--dependents controller
    if (!isDependentsQuestion) {
      this.handleRegularDoesNotApply(checkbox, questionId)
    }
  }

  // Handle DNA for regular text/radio questions
  // Note: Confirmation is now handled by tax-organizer--dna-confirmation controller
  handleRegularDoesNotApply(checkbox, questionId) {
    const input = this.questionInputTargets.find(i => i.dataset.questionId === questionId)

    if (!input) {
      console.warn(`No input found for question ID: ${questionId}`)
      return
    }

    if (checkbox.checked) {
      // Make input readonly when DNA is checked
      input.readOnly = true

      // For radio buttons, we need to uncheck and disable them
      if (input.type === 'radio') {
        const radioGroup = document.querySelectorAll(`input[name="${input.name}"]`)
        radioGroup.forEach(radio => {
          radio.checked = false
          radio.disabled = true
        })
      } else {
        // For text inputs, add visual styling to show they're disabled
        input.classList.add('bg-light', 'text-muted')
        input.style.cursor = 'not-allowed'
      }
    } else {
      // When unchecking DNA, restore the input
      input.readOnly = false

      // For radio buttons, re-enable them
      if (input.type === 'radio') {
        const radioGroup = document.querySelectorAll(`input[name="${input.name}"]`)
        radioGroup.forEach(radio => {
          radio.disabled = false
        })
      } else {
        // For text inputs, remove visual styling
        input.classList.remove('bg-light', 'text-muted')
        input.style.cursor = ''
      }
    }
  }

  // Update chevron icon when collapse state changes
  updateChevronIcon(collapseElement, isExpanded) {
    const targetId = collapseElement.id
    const header = this.element.querySelector(`[data-bs-target="#${targetId}"]`)

    if (header) {
      const icon = header.querySelector('.fa-chevron-up, .fa-chevron-down')
      if (icon) {
        // Create a new icon element - let color inherit from parent container
        const newIcon = document.createElement('i')
        const newIconClass = isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
        newIcon.className = newIconClass

        // Replace the old icon with the new one
        icon.parentNode.replaceChild(newIcon, icon)
      } else {
        console.warn(`No icon found for ${targetId}`)
      }

      header.setAttribute('aria-expanded', isExpanded.toString())
    } else {
      console.warn(`No header found for target ${targetId}`)
    }
  }

  // Set up event listeners for Bootstrap collapse events
  setupCollapseEventListeners() {
    document.addEventListener('shown.bs.collapse', (event) => {
      if (!this.element.contains(event.target)) return

      const collapseType = event.target.dataset.collapseType

      if (collapseType === 'section') {
        this.handleSectionShown(event.target)
      } else if (collapseType === 'question') {
        this.handleQuestionShown(event.target)
      }
    })

    document.addEventListener('hidden.bs.collapse', (event) => {
      if (!this.element.contains(event.target)) return

      const collapseType = event.target.dataset.collapseType

      if (collapseType === 'section') {
        this.handleSectionHidden(event.target)
      } else if (collapseType === 'question') {
        this.handleQuestionHidden(event.target)
      }
    })
  }

  handleSectionShown(collapseElement) {
    this.updateChevronIcon(collapseElement, true)
  }

  handleSectionHidden(collapseElement) {
    this.updateChevronIcon(collapseElement, false)
  }

  handleQuestionShown(collapseElement) {
    this.updateChevronIcon(collapseElement, true)

    const questionType = collapseElement.dataset.questionType
    if (questionType === 'text') {
      this.focusFirstTextInput(collapseElement)
    }
  }

  handleQuestionHidden(collapseElement) {
    this.updateChevronIcon(collapseElement, false)
  }

  focusFirstTextInput(collapseElement) {
    const textInput = collapseElement.querySelector('input[type="text"]')
    if (textInput) {
      textInput.focus()
    }
  }

  // Sign the engagement letter
  signEngagementLetter(event) {
    event.preventDefault()
    
    const signature = this.signatureInputTarget.value.trim()
    if (!signature) {
      alert('Please enter your signature')
      return
    }

    fetch(this.signUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ signature: signature })
    })
    .then(response => {
      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        throw new Error('Permission denied')
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        location.reload()
      } else {
        alert('Error signing engagement letter: ' + data.errors.join(', '))
      }
    })
    .catch(error => {
      console.error('Error signing engagement letter:', error)
      if (error.message !== 'Permission denied') {
        alert('Error signing engagement letter. Please try again.')
      }
    })
  }


  // Download engagement letter (placeholder)
  downloadEngagementLetter(event) {
    event.preventDefault()
    // This would generate and download a PDF of the engagement letter
    alert('PDF download functionality would be implemented here')
  }

  // Show success message
  showSuccessMessage(message) {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = 'toast align-items-center text-white bg-success border-0'
    toast.setAttribute('role', 'alert')
    toast.setAttribute('aria-live', 'assertive')
    toast.setAttribute('aria-atomic', 'true')
    
    toast.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">
          ${message}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    `
    
    // Add to page and show
    document.body.appendChild(toast)
    const bsToast = new bootstrap.Toast(toast)
    bsToast.show()
    
    // Remove after hiding
    toast.addEventListener('hidden.bs.toast', () => {
      document.body.removeChild(toast)
    })
  }

  getTaxYearFromUrl() {
    const path = window.location.pathname
    const match = path.match(/tax-organizers\/(\d{4})/)
    return match ? match[1] : new Date().getFullYear()
  }

  // Switch to a specific tab and scroll to tab container
  switchToTab(event) {
    const button = event.currentTarget
    const targetTabId = button.dataset.bsTarget

    // Activate the target tab using Bootstrap's Tab API
    const targetTab = document.querySelector(`[data-bs-target="${targetTabId}"]`)
    if (targetTab) {
      const tab = new bootstrap.Tab(targetTab)
      tab.show()
    }

    // Scroll to the tab navigation container smoothly
    // Target the actual ul element which has the scroll-margin-top style
    const tabContainer = document.querySelector('#taxOrganizerTabs')
    if (tabContainer) {
      tabContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Handle auto-save completion
  handleAutoSaveSuccess(data) {
    // Update bottom submit section if targets exist
    this.updateBottomSubmitSection(data)
  }

  // Update the bottom submit section based on auto-save response
  updateBottomSubmitSection(data) {
    if (!this.hasBottomSubmitButtonTarget || !this.hasBottomSubmitStatusTarget) return

    // Enable/disable submit button based on can_submit
    this.bottomSubmitButtonTarget.disabled = !data.can_submit

    // Update status message to match server-side rendering
    let statusHtml = ''

    if (data.can_submit) {
      // Ready to submit - matches server-side success message
      statusHtml = `
        <p class="text-success mt-2">
          <i class="fas fa-check-circle me-2"></i>Ready to submit! All requirements have been met.
        </p>
      `
    } else {
      // Build warning messages to match server-side style
      const warnings = []

      if (!data.engagement_signed) {
        warnings.push(`
          <p class="text-warning mb-1">
            <i class="fas fa-exclamation-triangle me-2"></i>Engagement letter must be signed before submitting.
          </p>
        `)
      }

      if (data.progress_percentage < this.minimumProgressValue) {
        warnings.push(`
          <p class="text-warning mb-1">
            <i class="fas fa-exclamation-triangle me-2"></i>Questionnaire must be at least ${this.minimumProgressValue}% complete (currently ${data.progress_percentage}%).
          </p>
        `)
      }

      // Wrap warnings in div with mt-2 class like server-side
      if (warnings.length > 0) {
        statusHtml = `<div class="mt-2">${warnings.join('')}</div>`
      }
    }

    this.bottomSubmitStatusTarget.innerHTML = statusHtml
  }
} 
